import React, { useEffect, useRef, useState } from "react";

/**
 * Categorised dropdown menu for chart indicators. Replaces the long inline
 * pill row. Indicators are grouped by family (Trend / Momentum / Volatility
 * / Volume / Drawings-like) for fast scanning.
 *
 * Props:
 *   active: array of active indicator keys
 *   onToggle(k): toggle one indicator
 *   onClear(): clear all
 */
const GROUPS = [
  {
    label: "Trend / Overlays",
    items: [
      { k: "ema20",  label: "EMA 20" },
      { k: "ema50",  label: "EMA 50" },
      { k: "ema200", label: "EMA 200" },
      { k: "vwap",   label: "VWAP" },
      { k: "bb",     label: "Bollinger Bands" },
      { k: "donchian",   label: "Donchian Channel" },
      { k: "supertrend", label: "SuperTrend" },
      { k: "pivots", label: "Pivot Points" },
      { k: "fib",    label: "Auto-Fibonacci" },
    ],
  },
  {
    label: "Momentum",
    items: [
      { k: "rsi",  label: "RSI" },
      { k: "macd", label: "MACD" },
      { k: "adx",  label: "ADX" },
    ],
  },
  {
    label: "Volatility",
    items: [
      { k: "atr", label: "ATR" },
    ],
  },
  {
    label: "Volume",
    items: [
      { k: "obv", label: "OBV" },
    ],
  },
];

export default function IndicatorMenu({ active, onToggle, onClear }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Click outside closes the menu so it doesn't fight the chart for focus.
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const activeCount = active.length;

  return (
    <div ref={ref} className="relative inline-block" data-testid="indicator-menu">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="indicator-menu-toggle"
        className={
          "inline-flex items-center gap-1.5 px-2.5 py-1 border rounded-sm text-[10px] font-mono uppercase tracking-widest transition " +
          (open
            ? "bg-mod text-bg border-mod"
            : "border-subtle text-secondary-fg hover:bg-surface-hover hover:border-mod/40")
        }
      >
        <span>Indicators</span>
        {activeCount > 0 && (
          <span className="inline-flex items-center justify-center min-w-[16px] h-3.5 px-1 rounded-sm bg-pos/20 text-pos text-[9px] tabular-nums">
            {activeCount}
          </span>
        )}
        <span className="opacity-60">{open ? "▴" : "▾"}</span>
      </button>

      {open && (
        <div
          className="absolute left-0 mt-1 z-40 w-[320px] max-h-[420px] overflow-y-auto border border-subtle rounded-sm bg-surface shadow-2xl shadow-black/40"
          data-testid="indicator-menu-panel"
        >
          <div className="sticky top-0 flex items-center justify-between px-3 py-2 border-b border-subtle bg-surface/95 backdrop-blur">
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">
              {activeCount} selected
            </span>
            {activeCount > 0 && (
              <button
                type="button"
                onClick={onClear}
                data-testid="indicator-menu-clear"
                className="text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:text-neg"
              >
                Clear all
              </button>
            )}
          </div>
          {GROUPS.map((g) => (
            <div key={g.label} className="px-2 py-2 border-b border-subtle last:border-b-0">
              <div className="px-2 pb-1 text-[9px] font-mono uppercase tracking-[0.2em] text-muted-fg/80">
                {g.label}
              </div>
              <div className="grid grid-cols-1 gap-0.5">
                {g.items.map((it) => {
                  const on = active.includes(it.k);
                  return (
                    <button
                      key={it.k}
                      type="button"
                      onClick={() => onToggle(it.k)}
                      data-testid={`indicator-${it.k}`}
                      className={
                        "flex items-center justify-between gap-2 px-2 py-1 rounded-sm text-[11px] font-mono transition " +
                        (on
                          ? "bg-mod/15 text-mod"
                          : "text-secondary-fg hover:bg-surface-hover")
                      }
                    >
                      <span>{it.label}</span>
                      <span className={"w-3.5 h-3.5 inline-flex items-center justify-center rounded-sm border " + (on ? "bg-mod border-mod text-bg text-[8px]" : "border-subtle")}>
                        {on ? "✓" : ""}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
